import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface GoogleAdProps {
  adSlot?: string;
  adFormat?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  adLayoutKey?: string;
  style?: React.CSSProperties;
  /** Classes for the outer slot wrapper (label + ad). */
  className?: string;
}

const isDev = import.meta.env.DEV;

/**
 * One AdSense unit with its "Advertisement" label.
 *
 * - Requests an ad only when the unit is actually laid out (width > 0); units
 *   inside hidden containers (e.g. `hidden md:block` on mobile) are skipped
 *   instead of throwing "No slot size for availableWidth=0".
 * - Keyed by pathname so client-side navigation between articles mounts a
 *   fresh <ins> and requests a new ad (an <ins> can only be filled once).
 * - The wrapper collapses via CSS when AdSense reports data-ad-status="unfilled"
 *   (see `.ad-slot` in index.css), so empty inventory leaves no blank box.
 */
const GoogleAd: React.FC<GoogleAdProps> = ({
  adSlot,
  adFormat = 'auto',
  adLayoutKey,
  style = { display: 'block' },
  className = ''
}) => {
  const { pathname } = useLocation();
  const insRef = useRef<HTMLModElement>(null);

  useEffect(() => {
    if (isDev) return; // don't push in dev — AdSense rejects localhost
    const ins = insRef.current;
    if (!ins || ins.offsetWidth === 0 || ins.dataset.adsbygoogleStatus) return;
    try {
      ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, [pathname]);

  // Show a labelled placeholder in dev so you can see ad placement
  if (isDev) {
    const heightMap: Record<string, string> = {
      rectangle: '250px',
      vertical: '600px',
      horizontal: '90px',
      auto: '150px',
    };
    return (
      <div
        className={`ad-slot ${className}`}
        style={{
          minHeight: heightMap[adFormat] ?? '150px',
          background: 'rgba(99,102,241,0.08)',
          border: '1px dashed rgba(99,102,241,0.4)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <span style={{ color: '#4338ca', fontSize: '11px', fontWeight: 600 }}>AD PLACEHOLDER (dev only)</span>
        <span style={{ color: '#475569', fontSize: '10px' }}>
          {adSlot ? `slot: ${adSlot}` : 'no slot ID'} · format: {adFormat}
        </span>
      </div>
    );
  }

  return (
    <div className={`ad-slot ${className}`}>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Advertisement</div>
      <ins
        key={pathname}
        ref={insRef}
        className="adsbygoogle"
        style={style}
        data-ad-client="ca-pub-8362860119627955"
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-ad-layout-key={adLayoutKey}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default GoogleAd;
